import React, { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  idea: { bg:'#F9F8F5', color:'#8A8A8A', label:'Idea' },
  planned: { bg:'#EDF3FF', color:'#3B5BDB', label:'Planned' },
  pending_approval: { bg:'#FFF3DC', color:'#8B5E0A', label:'Pending Approval' },
  approved: { bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  in_production: { bg:'#F0F0FF', color:'#5C5FDB', label:'In Production' },
  ready_for_review: { bg:'#FFF0F6', color:'#C2255C', label:'Ready for Review' },
  approved_to_publish: { bg:'#D3F9D8', color:'#2B8A3E', label:'Approved to Publish' },
  published: { bg:'#556B2F22', color:'#556B2F', label:'Published' },
  performance_recorded: { bg:'#A8893D22', color:'#A8893D', label:'Performance Recorded' },
  rejected: { bg:'#FDEAEA', color:'#8F2E24', label:'Rejected' },
  revision_required: { bg:'#FFF0E0', color:'#D6720B', label:'Revision Required' },
  cancelled: { bg:'#F5F5F5', color:'#aaa', label:'Cancelled' },
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function BrandContent() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const isManager = user?.role === 'sales_manager';
  const isBCO = user?.role === 'brand_content_officer';
  const canApprove = isSuperAdmin || isManager;
  const canEdit = isBCO || isSuperAdmin;

  const [content, setContent] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | calendar
  const [selectedContent, setSelectedContent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState(searchParams.get('filter')||'');
  const [search, setSearch] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [approvalComment, setApprovalComment] = useState('');
  const [showApproval, setShowApproval] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [performanceForm, setPerformanceForm] = useState({});
  const [showPerformance, setShowPerformance] = useState(false);

  const [form, setForm] = useState({
    title:'', description:'', pillar_id:'', campaign_id:'',
    product_name:'', platform_id:'', content_type_id:'',
    objective:'', target_audience:'', shoot_required:false,
    shoot_date:'', required_products:'', required_props:'',
    required_location:'', script:'', caption:'',
    assigned_creator:'', due_date:'', planned_pub_date:''
  });

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.append('status', filterStatus);
    if (search) params.append('search', search);
    Promise.all([
      get('/bc/content?' + params.toString()),
      config ? Promise.resolve(config) : get('/bc/config'),
    ]).then(([c, cfg]) => {
      setContent(c.data||[]);
      if (!config) setConfig(cfg);
    }).finally(() => setLoading(false));
  }, [filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (item) => {
    const detail = await get('/bc/content/'+item.id);
    setSelectedContent(detail);
    setShowDetail(true);
  };

  const openForm = (item=null) => {
    if (item) {
      setForm({
        title:item.title||'', description:item.description||'',
        pillar_id:item.pillar_id||'', campaign_id:item.campaign_id||'',
        product_name:item.product_name||'', platform_id:item.platform_id||'',
        content_type_id:item.content_type_id||'', objective:item.objective||'',
        target_audience:item.target_audience||'',
        shoot_required:item.shoot_required||false,
        shoot_date:item.shoot_date?.split('T')[0]||'',
        required_products:item.required_products||'',
        required_props:item.required_props||'',
        required_location:item.required_location||'',
        script:item.script||'', caption:item.caption||'',
        assigned_creator:item.assigned_creator||'',
        due_date:item.due_date?.split('T')[0]||'',
        planned_pub_date:item.planned_pub_date?.split('T')[0]||'',
      });
      setSelectedContent(item);
    } else {
      setForm({title:'',description:'',pillar_id:'',campaign_id:'',
        product_name:'',platform_id:'',content_type_id:'',objective:'',
        target_audience:'',shoot_required:false,shoot_date:'',
        required_products:'',required_props:'',required_location:'',
        script:'',caption:'',assigned_creator:'',due_date:'',planned_pub_date:''});
      setSelectedContent(null);
    }
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title) { alert('Title required'); return; }
    setSaving(true);
    try {
      if (selectedContent?.id) {
        await put('/bc/content/'+selectedContent.id, form);
      } else {
        await post('/bc/content', form);
      }
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await put('/bc/content/'+id+'/status', { status, comment: approvalComment });
      setShowApproval(false);
      setApprovalComment('');
      setShowDetail(false);
      load();
    } catch(err) { alert(err.message); }
  };

  const recordPerformance = async (id) => {
    setSaving(true);
    try {
      await post('/bc/content/'+id+'/performance', performanceForm);
      setShowPerformance(false);
      setPerformanceForm({});
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  // Calendar helpers
  const getCalendarDays = () => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth+1, 0).getDate();
    const days = [];
    for (let i=0; i<firstDay; i++) days.push(null);
    for (let i=1; i<=daysInMonth; i++) days.push(i);
    return days;
  };

  const getContentForDay = (day) => {
    if (!day) return [];
    const dateStr = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return content.filter(c =>
      c.planned_pub_date?.startsWith(dateStr) ||
      c.due_date?.startsWith(dateStr) ||
      c.shoot_date?.startsWith(dateStr)
    );
  };

  const sl = (obj) => Object.entries(obj);

  return (
    <div className="page">
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',
            color:'#2C1F16',margin:'0 0 4px'}}>Content</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Plan, manage and track all content</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div style={{display:'flex',border:'1px solid #ddd',borderRadius:6,overflow:'hidden'}}>
            {['list','calendar'].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:'6px 14px',border:'none',fontSize:11,cursor:'pointer',
                  background:view===v?'#556B2F':'#fff',
                  color:view===v?'#fff':'#555',fontWeight:view===v?600:400}}>
                {v==='list'?'List':'Calendar'}
              </button>
            ))}
          </div>
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={()=>openForm()}>
              + New Content
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input className="form-control" placeholder="Search..." value={search}
          onChange={e=>setSearch(e.target.value)}
          style={{maxWidth:200,fontSize:12}} />
        <select className="form-control" value={filterStatus}
          onChange={e=>setFilterStatus(e.target.value)}
          style={{maxWidth:180,fontSize:12}}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_COLORS).map(([k,v])=>(
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* LIST VIEW */}
      {view==='list' && (
        loading ? <div className="spinner"/> :
        content.length === 0 ? (
          <div className="empty-state">
            <h3>No content yet</h3>
            {canEdit && <p>Click + New Content to create your first content record</p>}
          </div>
        ) : (
          <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#2C1F16'}}>
                  {['Title','Platform','Type','Status','Due','Pub Date',''].map(h=>(
                    <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {content.map((c,i)=>{
                  const st = STATUS_COLORS[c.status]||STATUS_COLORS.idea;
                  const isOverdue = c.due_date && new Date(c.due_date) < new Date() &&
                    !['published','cancelled','performance_recorded'].includes(c.status);
                  return (
                    <tr key={c.id} style={{background:i%2===0?'#fff':'#F9F8F5',cursor:'pointer'}}
                      onClick={()=>openDetail(c)}>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontWeight:600,color:'#2C1F16',maxWidth:200}}>
                        <div style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {c.title}
                        </div>
                        {c.campaign_name && (
                          <div style={{fontSize:10,color:'#A8893D',marginTop:2}}>{c.campaign_name}</div>
                        )}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                        {c.platform_icon} {c.platform_name||'—'}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                        {c.content_type_name||'—'}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,
                          background:st.bg,color:st.color}}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        color:isOverdue?'#8F2E24':'#555',fontWeight:isOverdue?600:400}}>
                        {c.due_date ? new Date(c.due_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'}) : '—'}
                        {isOverdue && ' ⚠'}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                        {c.planned_pub_date ? new Date(c.planned_pub_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'}) : '—'}
                      </td>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#A8893D'}}>→</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* CALENDAR VIEW */}
      {view==='calendar' && (
        <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
          {/* Calendar header */}
          <div style={{background:'#2C1F16',padding:'14px 20px',
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <button onClick={()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }}
              style={{background:'none',border:'none',color:'#C9A96E',cursor:'pointer',fontSize:16}}>‹</button>
            <div style={{fontSize:14,fontWeight:600,color:'#F2EFE7'}}>
              {MONTHS[calMonth]} {calYear}
            </div>
            <button onClick={()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }}
              style={{background:'none',border:'none',color:'#C9A96E',cursor:'pointer',fontSize:16}}>›</button>
          </div>
          {/* Day headers */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',
            background:'#F9F8F5',borderBottom:'1px solid #E8E0D4'}}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
              <div key={d} style={{padding:'8px',textAlign:'center',fontSize:10,
                color:'#8A8A8A',fontWeight:600,textTransform:'uppercase'}}>{d}</div>
            ))}
          </div>
          {/* Days grid */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)'}}>
            {getCalendarDays().map((day,i)=>{
              const dayContent = day ? getContentForDay(day) : [];
              const isToday = day && new Date().getDate()===day &&
                new Date().getMonth()===calMonth && new Date().getFullYear()===calYear;
              return (
                <div key={i} style={{minHeight:90,padding:6,
                  borderRight:'1px solid #F2EFE7',borderBottom:'1px solid #F2EFE7',
                  background:isToday?'#F0F7F1':'#fff'}}>
                  {day && (
                    <>
                      <div style={{fontSize:11,fontWeight:isToday?700:400,
                        color:isToday?'#556B2F':'#555',marginBottom:4}}>{day}</div>
                      {dayContent.slice(0,2).map(c=>{
                        const st = STATUS_COLORS[c.status]||STATUS_COLORS.idea;
                        return (
                          <div key={c.id} onClick={()=>openDetail(c)}
                            style={{fontSize:9,background:st.bg,color:st.color,
                              padding:'2px 6px',borderRadius:4,marginBottom:2,
                              cursor:'pointer',whiteSpace:'nowrap',overflow:'hidden',
                              textOverflow:'ellipsis',fontWeight:600}}>
                            {c.platform_icon} {c.title}
                          </div>
                        );
                      })}
                      {dayContent.length > 2 && (
                        <div style={{fontSize:9,color:'#8A8A8A',marginTop:2}}>
                          +{dayContent.length-2} more
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CONTENT DETAIL MODAL */}
      {showDetail && selectedContent?.data && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:720,width:'92vw'}}>
            <div className="modal-hdr">
              <div>
                <div style={{fontSize:11,color:'#8A8A8A',marginBottom:2}}>
                  {selectedContent.data.platform_icon} {selectedContent.data.platform_name} · {selectedContent.data.content_type_name}
                </div>
                <span className="modal-title">{selectedContent.data.title}</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDetail(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              {/* Status */}
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
                {(() => {
                  const st = STATUS_COLORS[selectedContent.data.status]||STATUS_COLORS.idea;
                  return <span style={{fontSize:11,padding:'4px 12px',borderRadius:20,
                    fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>;
                })()}
                {selectedContent.data.campaign_name && (
                  <span style={{fontSize:11,color:'#A8893D'}}>📢 {selectedContent.data.campaign_name}</span>
                )}
                {selectedContent.data.pillar_name && (
                  <span style={{fontSize:11,color:'#8A8A8A'}}>🏛 {selectedContent.data.pillar_name}</span>
                )}
              </div>

              {selectedContent.data.description && (
                <p style={{fontSize:13,color:'#555',marginBottom:16,lineHeight:1.6}}>
                  {selectedContent.data.description}
                </p>
              )}

              {/* Details grid */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                {[
                  ['Due Date', selectedContent.data.due_date ? new Date(selectedContent.data.due_date).toLocaleDateString('en-NG') : '—'],
                  ['Planned Publication', selectedContent.data.planned_pub_date ? new Date(selectedContent.data.planned_pub_date).toLocaleDateString('en-NG') : '—'],
                  ['Target Audience', selectedContent.data.target_audience||'—'],
                  ['Objective', selectedContent.data.objective||'—'],
                  ['Shoot Required', selectedContent.data.shoot_required ? `Yes — ${selectedContent.data.shoot_date ? new Date(selectedContent.data.shoot_date).toLocaleDateString('en-NG') : 'Date TBD'}` : 'No'],
                  ['Location', selectedContent.data.required_location||'—'],
                ].map(([label,val])=>(
                  <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:10}}>
                    <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',
                      letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:12,color:'#2C1F16',fontWeight:500}}>{val}</div>
                  </div>
                ))}
              </div>

              {selectedContent.data.caption && (
                <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Caption / Copy</div>
                  <div style={{fontSize:12,color:'#2C1F16',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{selectedContent.data.caption}</div>
                </div>
              )}

              {/* Linked tasks */}
              {selectedContent.tasks?.length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>
                    Linked Tasks ({selectedContent.tasks.length})
                  </div>
                  {selectedContent.tasks.map(t=>(
                    <div key={t.task_id} style={{display:'flex',justifyContent:'space-between',
                      padding:'6px 10px',background:'#F9F8F5',borderRadius:6,marginBottom:4}}>
                      <span style={{fontSize:12,color:'#2C1F16'}}>{t.title}</span>
                      <span style={{fontSize:10,color:t.task_status==='completed'?'#2E6B3A':'#8B5E0A'}}>
                        {t.task_status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Approval history */}
              {selectedContent.approvals?.length > 0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>
                    Approval History
                  </div>
                  {selectedContent.approvals.map(a=>(
                    <div key={a.id} style={{display:'flex',gap:10,padding:'6px 10px',
                      background:'#F9F8F5',borderRadius:6,marginBottom:4}}>
                      <span style={{fontSize:11,color:'#555',flex:1}}>
                        <strong>{a.actioned_by_name}</strong> → {a.to_status?.replace(/_/g,' ')}
                      </span>
                      {a.comment && <span style={{fontSize:11,color:'#8A8A8A',fontStyle:'italic'}}>{a.comment}</span>}
                      <span style={{fontSize:10,color:'#8A8A8A',flexShrink:0}}>
                        {new Date(a.created_at).toLocaleDateString('en-NG')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Performance */}
              {selectedContent.performance && (
                <div style={{background:'#F9F4E8',border:'1px solid #E8D5A0',
                  borderRadius:8,padding:12,marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#A8893D',
                    textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>
                    Performance
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
                    {[
                      ['Reach',selectedContent.performance.reach],
                      ['Impressions',selectedContent.performance.impressions],
                      ['Likes',selectedContent.performance.likes],
                      ['Leads',selectedContent.performance.leads_generated],
                    ].map(([label,val])=>(
                      <div key={label} style={{textAlign:'center'}}>
                        <div style={{fontSize:16,fontWeight:700,color:'#A8893D'}}>{val||0}</div>
                        <div style={{fontSize:9,color:'#8A8A8A'}}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-ftr" style={{flexWrap:'wrap',gap:8}}>
              {canEdit && (
                <button className="btn btn-outline btn-sm"
                  onClick={()=>{setShowDetail(false);openForm(selectedContent.data);}}>
                  Edit
                </button>
              )}
              {/* BCO status actions */}
              {canEdit && selectedContent.data.status === 'idea' && (
                <button className="btn btn-outline btn-sm" style={{color:'#3B5BDB'}}
                  onClick={()=>updateStatus(selectedContent.data.id,'planned')}>
                  Mark Planned
                </button>
              )}
              {canEdit && selectedContent.data.status === 'planned' && (
                <button className="btn btn-primary btn-sm"
                  onClick={()=>updateStatus(selectedContent.data.id,'pending_approval')}>
                  Submit for Approval
                </button>
              )}
              {canEdit && selectedContent.data.status === 'approved' && (
                <button className="btn btn-outline btn-sm" style={{color:'#5C5FDB'}}
                  onClick={()=>updateStatus(selectedContent.data.id,'in_production')}>
                  Start Production
                </button>
              )}
              {canEdit && selectedContent.data.status === 'in_production' && (
                <button className="btn btn-outline btn-sm" style={{color:'#C2255C'}}
                  onClick={()=>updateStatus(selectedContent.data.id,'ready_for_review')}>
                  Ready for Review
                </button>
              )}
              {canEdit && selectedContent.data.status === 'approved_to_publish' && (
                <button className="btn btn-primary btn-sm"
                  onClick={()=>updateStatus(selectedContent.data.id,'published')}>
                  Mark Published
                </button>
              )}
              {canEdit && selectedContent.data.status === 'published' && !selectedContent.performance && (
                <button className="btn btn-outline btn-sm" style={{color:'#A8893D'}}
                  onClick={()=>setShowPerformance(true)}>
                  Record Performance
                </button>
              )}
              {/* Approver actions */}
              {canApprove && selectedContent.data.status === 'pending_approval' && (
                <>
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>{setApprovalAction('approved');setShowApproval(true);}}>
                    Approve
                  </button>
                  <button className="btn btn-outline btn-sm" style={{color:'#D6720B'}}
                    onClick={()=>{setApprovalAction('revision_required');setShowApproval(true);}}>
                    Request Revision
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                    onClick={()=>{setApprovalAction('rejected');setShowApproval(true);}}>
                    Reject
                  </button>
                </>
              )}
              {canApprove && selectedContent.data.status === 'ready_for_review' && (
                <button className="btn btn-primary btn-sm"
                  onClick={()=>{setApprovalAction('approved_to_publish');setShowApproval(true);}}>
                  Approve to Publish
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* APPROVAL COMMENT MODAL */}
      {showApproval && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-hdr">
              <span className="modal-title">
                {approvalAction==='approved'?'Approve Content':
                 approvalAction==='approved_to_publish'?'Approve to Publish':
                 approvalAction==='revision_required'?'Request Revision':'Reject Content'}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowApproval(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Comment {approvalAction!=='approved'?'*':''}</label>
                <textarea className="form-control" rows={3}
                  value={approvalComment}
                  onChange={e=>setApprovalComment(e.target.value)}
                  placeholder={approvalAction==='approved'?'Optional comment...':'Please explain...'}/>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowApproval(false)}>Cancel</button>
              <button className="btn btn-primary"
                style={{background:approvalAction==='rejected'?'#8F2E24':approvalAction==='revision_required'?'#D6720B':'#556B2F',border:'none'}}
                onClick={()=>updateStatus(selectedContent.data.id, approvalAction)}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERFORMANCE MODAL */}
      {showPerformance && selectedContent?.data && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Record Performance</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPerformance(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{fontSize:12,color:'#8A8A8A',marginBottom:16}}>
                {selectedContent.data.title} · {selectedContent.data.platform_name}
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {['reach','impressions','views','engagement','likes','comments','shares','saves','clicks','enquiries_generated','leads_generated'].map(field=>(
                  <div key={field} className="form-group">
                    <label className="form-label" style={{textTransform:'capitalize'}}>
                      {field.replace(/_/g,' ')}
                    </label>
                    <input type="number" className="form-control" min="0"
                      value={performanceForm[field]||''}
                      onChange={e=>setPerformanceForm({...performanceForm,[field]:parseInt(e.target.value)||0})} />
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2}
                  value={performanceForm.notes||''}
                  onChange={e=>setPerformanceForm({...performanceForm,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowPerformance(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>recordPerformance(selectedContent.data.id)} disabled={saving}>
                {saving?'Saving...':'Save Performance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONTENT FORM MODAL */}
      {showForm && config && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:760,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{selectedContent?.id?'Edit Content':'New Content'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              {/* Basic info */}
              <div style={{fontSize:10,fontWeight:700,color:'#556B2F',
                textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>
                Basic Information
              </div>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-control" value={form.title}
                  onChange={e=>setForm({...form,title:e.target.value})}
                  placeholder="e.g. Resort Tote Product Reel" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description}
                  onChange={e=>setForm({...form,description:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Platform</label>
                  <select className="form-control" value={form.platform_id}
                    onChange={e=>setForm({...form,platform_id:e.target.value})}>
                    <option value="">Select platform</option>
                    {config.platforms.map(p=><option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Content Type</label>
                  <select className="form-control" value={form.content_type_id}
                    onChange={e=>setForm({...form,content_type_id:e.target.value})}>
                    <option value="">Select type</option>
                    {config.content_types.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Content Pillar</label>
                  <select className="form-control" value={form.pillar_id}
                    onChange={e=>setForm({...form,pillar_id:e.target.value})}>
                    <option value="">Select pillar</option>
                    {config.pillars.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Campaign</label>
                  <select className="form-control" value={form.campaign_id}
                    onChange={e=>setForm({...form,campaign_id:e.target.value})}>
                    <option value="">No campaign</option>
                    {config.campaigns.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product</label>
                  <input className="form-control" value={form.product_name}
                    onChange={e=>setForm({...form,product_name:e.target.value})}
                    placeholder="Product name" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Objective</label>
                  <input className="form-control" value={form.objective}
                    onChange={e=>setForm({...form,objective:e.target.value})}
                    placeholder="e.g. Brand awareness, Lead generation" />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Audience</label>
                  <input className="form-control" value={form.target_audience}
                    onChange={e=>setForm({...form,target_audience:e.target.value})} />
                </div>
              </div>

              {/* Production info */}
              <div style={{fontSize:10,fontWeight:700,color:'#556B2F',
                textTransform:'uppercase',letterSpacing:'.08em',margin:'16px 0 10px'}}>
                Production
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-control" value={form.due_date}
                    onChange={e=>setForm({...form,due_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Planned Publication</label>
                  <input type="date" className="form-control" value={form.planned_pub_date}
                    onChange={e=>setForm({...form,planned_pub_date:e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{display:'flex',alignItems:'center',gap:8,paddingTop:20}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
                    <input type="checkbox" checked={form.shoot_required}
                      onChange={e=>setForm({...form,shoot_required:e.target.checked})}
                      style={{width:16,height:16,accentColor:'#556B2F'}} />
                    Shoot Required
                  </label>
                </div>
                {form.shoot_required && (
                  <div className="form-group">
                    <label className="form-label">Shoot Date</label>
                    <input type="date" className="form-control" value={form.shoot_date}
                      onChange={e=>setForm({...form,shoot_date:e.target.value})} />
                  </div>
                )}
              </div>
              {form.shoot_required && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Required Products</label>
                      <input className="form-control" value={form.required_products}
                        onChange={e=>setForm({...form,required_products:e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Required Props</label>
                      <input className="form-control" value={form.required_props}
                        onChange={e=>setForm({...form,required_props:e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <input className="form-control" value={form.required_location}
                        onChange={e=>setForm({...form,required_location:e.target.value})} />
                    </div>
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Caption / Copy</label>
                <textarea className="form-control" rows={3} value={form.caption}
                  onChange={e=>setForm({...form,caption:e.target.value})}
                  placeholder="Write the caption or copy for this content..." />
              </div>
              <div className="form-group">
                <label className="form-label">Script</label>
                <textarea className="form-control" rows={3} value={form.script}
                  onChange={e=>setForm({...form,script:e.target.value})}
                  placeholder="Script or talking points..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':selectedContent?.id?'Save Changes':'Create Content'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
